// Lookup values, seed/demo data, and record-shape helpers.
// In production these lookups come from GET /api/lookup/*; the arrays
// here still back the offline/dev fallback and the wizard's dropdowns.
import type { ClientRecord, RecStatus, SpecimenRows } from "../types";

/*  REFERENCE / LOOKUP VALUES (from the FlCare ERD lookup tables)      */
/* ------------------------------------------------------------------ */

export const SOURCES: string[] = ["Ehars", "Case Report", "Self-Reported"];
export const BIRTH_SEXES: string[] = ["Male", "Female", "Unknown"];
export const ADDRESS_TYPES: string[] = ["Home Address", "Mailing Address", "Work Address", "Temporary Address"];
export const COUNTIES: string[] = ["County 1", "County 2", "County 3", "County 4", "County 5", "County 6", "County 7", "County 8", "County 9"];
export const RELATIONSHIPS: string[] = ["Spouse", "Parent", "Child", "Sibling", "Friend", "Guardian", "Other"];
export const LINKAGE_STATUSES: string[] = ["Already in Care", "HIV Negative", "Referred to EHE", "NA", "Pending", "Out of care, referred to Ryan White Program", "Others"];
export const MODES_OF_TRANSMISSION: string[] = ["Adult MSM", "Adult IDU", "Adult MSM & IDU", "Adult Heterosexual Contact", "Perinatal", "Other/Unknown"];
export const TREATMENTS: string[] = ["None", "Doxycycline", "Penicillin", "Azithromycin", "Ceftriaxone", "Other"];
export const REACTIVITY_RESULTS: string[] = ["Reactive", "Non-Reactive", "Positive", "Negative", "Indeterminate"];

export const HOSPITAL_FACILITIES: Record<string, string[]> = {
  "Hospital A": ["Facility A1", "Facility A2", "Facility A3"],
  "Hospital B": ["Facility B1"],
  "Hospital C": ["Facility C1"],
  "Hospital D": ["Facility D1"],
};
export const HOSPITALS: string[] = Object.keys(HOSPITAL_FACILITIES);

/* ------------------------------------------------------------------ */
/*  RECORD SHAPE — mirrors tblClient / tblCaseReport / tblSTDInfo      */
/* ------------------------------------------------------------------ */

export function emptySpecimenRows(): SpecimenRows {
  return { rpr: [], vdrl: [], tppa: [], ftaAbs: [], eia: [], chlamydia: [], gonorrhea: [] };
}

export const emptyRecord: ClientRecord = {
  id: null,
  source: "Ehars",
  firstName: "", middleName: "", lastName: "",
  dob: "", birthSex: "",
  hospital: "", facility: "",
  dateReceived: "", medicalRecordNumber: "", eharsId: "",

  addressType: "Home Address", street1: "", street2: "", city: "", state: "FL", zip: "", county: "",
  primaryPhone: "", alternatePhone: "", contactComments: "",

  ecName: "", ecRelationship: "", ecPhone: "",

  medication: "", inCare: "", disclosed: "", pregnant: "", edd: "", dateLMP: "", medicalNotes: "",

  linkageStatus: "", updatedLinkageStatus: "", linkageOtherDetails: "", notificationSent: false, dateOfNotification: "",

  testFlags: { t1PositiveTest: true, t2CaseReport: false, isSelfReported: false, isPositiveTest: false, isCaseReport: false },

  treatmentText: "", naatResult: "", naatDate: "", disclosedDate: "", hivAgAbResult: "", hivAgAbDate: "", inCareDate: "", hivIaResult: "", hivIaDate: "",

  hiv: { diagnosisDate: "", aidsDate: "", modeOfTransmission: "", reportedDate: "", lastViralLoadDate: "", viralLoad: "", lastCd4Date: "", cd4Count: "" },
  hivUpdated: { diagnosisDate: "", aidsDate: "", modeOfTransmission: "", reportedDate: "", lastViralLoadDate: "", viralLoad: "", lastCd4Date: "", cd4Count: "" },

  treatmentGiven: "", treatmentDate: "", treatmentNotes: "",

  stdTests: emptySpecimenRows(),
  stdTestsUpdated: emptySpecimenRows(),

  additionalNotes: "", dohNotes: "",

  providerMRN: "", starsProfileId: "", recStatus: "New",
  linkageGridStatus: "", pendingDays: null,
  createdBy: "", createdDate: "", lastUpdatedBy: "", lastUpdatedDate: "",
};

/* ------------------------------------------------------------------ */
/*  SEED DATA                                                          */
/* ------------------------------------------------------------------ */

export const sampleRecord: ClientRecord = {
  ...emptyRecord,
  id: "REC-0001",
  source: "Ehars",
  firstName: "Jane", middleName: "", lastName: "Doe",
  dob: "1985-01-15", birthSex: "Female",
  hospital: "Hospital A", facility: "Facility A1",
  dateReceived: "2026-01-10", medicalRecordNumber: "MR-000001", eharsId: "SAMPLE001",

  addressType: "Home Address", street1: "123 Main St", street2: "", city: "Sample City", state: "FL", zip: "00000", county: "County 1",
  primaryPhone: "(000) 000-0001", alternatePhone: "", contactComments: "",

  ecName: "", ecRelationship: "", ecPhone: "",

  medication: "", inCare: "Yes", disclosed: "Yes", pregnant: "N/A", edd: "", dateLMP: "", medicalNotes: "",

  linkageStatus: "HIV Negative", updatedLinkageStatus: "", linkageOtherDetails: "", notificationSent: true, dateOfNotification: "2026-01-10",

  testFlags: { t1PositiveTest: true, t2CaseReport: false, isSelfReported: false, isPositiveTest: true, isCaseReport: false },

  treatmentText: "Started on ART regimen", naatResult: "Non-Reactive", naatDate: "2020-01-01", disclosedDate: "2020-01-05",
  hivAgAbResult: "Reactive", hivAgAbDate: "2020-01-01", inCareDate: "2020-01-10", hivIaResult: "Reactive", hivIaDate: "2020-01-02",

  hiv: {
    diagnosisDate: "2020-01-01", aidsDate: "2020-01-01", modeOfTransmission: "Adult MSM",
    reportedDate: "2020-01-15", lastViralLoadDate: "2025-01-01", viralLoad: "Sample Value",
    lastCd4Date: "2025-01-01", cd4Count: "500",
  },
  hivUpdated: { diagnosisDate: "", aidsDate: "", modeOfTransmission: "", reportedDate: "", lastViralLoadDate: "", viralLoad: "", lastCd4Date: "", cd4Count: "" },

  treatmentGiven: "", treatmentDate: "", treatmentNotes: "",

  stdTests: {
    rpr: [
      { date: "2025-01-01", specimenId: "", details: "Sample Result", titer: "1:1" },
      { date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "1:1" },
    ],
    vdrl: [],
    tppa: [{ date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "" }],
    ftaAbs: [
      { date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "" },
    ],
    eia: [{ date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "1:1" }],
    chlamydia: [
      { date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "" },
    ],
    gonorrhea: [
      { date: "2024-01-01", specimenId: "", details: "Sample Result", titer: "" },
    ],
  },
  stdTestsUpdated: emptySpecimenRows(),

  additionalNotes: "", dohNotes: "",

  providerMRN: "000001", starsProfileId: "", recStatus: "Updated",
  linkageGridStatus: "HIV Negative", pendingDays: null,
  createdBy: "User A", createdDate: "2026-01-10", lastUpdatedBy: "User A", lastUpdatedDate: "2026-01-11",
};

export interface GridRecordOverrides {
  id: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  birthSex?: string;
  hospital: string;
  facility: string;
  street1?: string;
  city?: string;
  county?: string;
  providerMRN: string;
  starsProfileId?: string;
  eharsId?: string;
  recStatus: RecStatus;
  linkageGridStatus: string;
  pendingDays?: number | null;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export function makeGridRecord(over: GridRecordOverrides): ClientRecord {
  return {
    ...emptyRecord,
    id: over.id,
    firstName: over.firstName || "Patient", lastName: over.lastName || "Record",
    dob: over.dob || "1990-01-01", birthSex: over.birthSex || "Unknown",
    hospital: over.hospital, facility: over.facility,
    street1: over.street1 || "", city: over.city || "", county: over.county || "",
    providerMRN: over.providerMRN, starsProfileId: over.starsProfileId || "",
    eharsId: over.eharsId || "", recStatus: over.recStatus,
    linkageGridStatus: over.linkageGridStatus, pendingDays: over.pendingDays ?? null,
    createdBy: over.createdBy, createdDate: over.createdDate, lastUpdatedBy: over.lastUpdatedBy || "", lastUpdatedDate: over.lastUpdatedDate || "",
  };
}

export const seedRecords: ClientRecord[] = [
  sampleRecord,
  makeGridRecord({ id: "REC-0002", firstName: "John", lastName: "Smith", hospital: "Hospital A", facility: "Facility A1", providerMRN: "000002", recStatus: "New", linkageGridStatus: "Already in Care", createdBy: "User A", createdDate: "2026-01-11" }),
  makeGridRecord({ id: "REC-0003", firstName: "Alex", lastName: "Johnson", hospital: "Hospital A", facility: "Facility A1", providerMRN: "000003", recStatus: "New", linkageGridStatus: "Referred to EHE", createdBy: "User B", createdDate: "2026-01-12" }),
  makeGridRecord({ id: "REC-0004", firstName: "Sam", lastName: "Lee", hospital: "Hospital B", facility: "Facility B1", providerMRN: "000004", starsProfileId: "100004", recStatus: "Updated", linkageGridStatus: "Referred to EHE", createdBy: "User B", createdDate: "2026-01-13", lastUpdatedBy: "User B", lastUpdatedDate: "2026-01-14" }),
  makeGridRecord({ id: "REC-0005", firstName: "Taylor", lastName: "Brown", hospital: "Hospital C", facility: "Facility C1", providerMRN: "000005", starsProfileId: "100005", recStatus: "Updated", linkageGridStatus: "Referred to EHE", createdBy: "User A", createdDate: "2026-01-14", lastUpdatedBy: "User B", lastUpdatedDate: "2026-01-16" }),
  makeGridRecord({ id: "REC-0006", firstName: "Jordan", lastName: "Davis", hospital: "Hospital B", facility: "Facility B1", providerMRN: "000006", recStatus: "New", linkageGridStatus: "NA", createdBy: "User B", createdDate: "2026-01-15" }),
  makeGridRecord({ id: "REC-0007", firstName: "Morgan", lastName: "Clark", hospital: "Hospital A", facility: "Facility A2", providerMRN: "000007", recStatus: "New", linkageGridStatus: "HIV Negative", createdBy: "User A", createdDate: "2026-01-16" }),
  makeGridRecord({ id: "REC-0008", firstName: "Casey", lastName: "Wilson", hospital: "Hospital B", facility: "Facility B1", providerMRN: "000008", recStatus: "New", linkageGridStatus: "Pending", pendingDays: 3, createdBy: "User B", createdDate: "2026-01-17", lastUpdatedBy: "User B", lastUpdatedDate: "2026-01-18" }),
  makeGridRecord({ id: "REC-0009", firstName: "Riley", lastName: "Moore", hospital: "Hospital A", facility: "Facility A1", providerMRN: "000009", recStatus: "New", linkageGridStatus: "Out of care, referred to Ryan White Program", createdBy: "User A", createdDate: "2026-01-18" }),
  makeGridRecord({ id: "REC-0010", firstName: "Drew", lastName: "Taylor", hospital: "Hospital D", facility: "Facility D1", providerMRN: "000010", starsProfileId: "100010", recStatus: "Updated", linkageGridStatus: "Others", createdBy: "User B", createdDate: "2026-01-19", lastUpdatedBy: "User B", lastUpdatedDate: "2026-01-20" }),
  makeGridRecord({ id: "REC-0011", firstName: "Cameron", lastName: "White", hospital: "Hospital A", facility: "Facility A3", providerMRN: "000011", recStatus: "New", linkageGridStatus: "Pending", pendingDays: 21, createdBy: "User C", createdDate: "2026-01-20" }),
  makeGridRecord({ id: "REC-0012", firstName: "Jamie", lastName: "Anderson", hospital: "Hospital B", facility: "Facility B1", providerMRN: "000012", starsProfileId: "", eharsId: "SAMPLE012", recStatus: "Updated", linkageGridStatus: "Pending", pendingDays: 18, createdBy: "User A", createdDate: "2026-01-21", lastUpdatedBy: "User B", lastUpdatedDate: "2026-01-23" }),
];

